import * as React from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import MainLayout from "@/components/template/dashboard-layout";
import LeaderBoard from "@/components/organisms/leader-board";
import NotFoundPage from "./404";
import {
  MenuType,
  LeaderboardMetric,
  LeaderboardItemType,
} from "@/types/leader-board";
import MotionModal from "@/components/atomics/motion-modal";
import ShareInfoModal from "@/components/molecules/leader-board/share-info-modal";
import {
  fetchOwnerUserData,
  fetchCollectionDataByAddress,
  fetchNftDataByAddress,
} from "@/utils/leaderboardDataFetcher";

export default function LeaderBoardPage() {
  const { type, order_param } = useParams<{
    type?: string;
    order_param?: LeaderboardMetric;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUserInfo, setShareUserInfo] =
    useState<LeaderboardItemType | null>(null);

  // Determine current type: if type param exists, use it; otherwise default to collections
  const currentType: MenuType = (type as MenuType) || "collections";

  const getDefaultSort = (menuType: MenuType): LeaderboardMetric => {
    switch (menuType) {
      case "users":
        return "all_stars";
      case "collections":
        return "all_stars";
      case "nft":
        return "all_stars";
      default:
        return "all_stars";
    }
  };

  // Use order_param from URL path, fallback to query param for backward compatibility, then default
  const currentSort =
    (order_param as LeaderboardMetric) ||
    (searchParams.get("sort") as LeaderboardMetric) ||
    getDefaultSort(currentType);

  const validTypes: MenuType[] = ["users", "collections", "nft"];
  if (type && !validTypes.includes(type as MenuType)) {
    return <NotFoundPage />;
  }

  const validSorts: Record<MenuType, LeaderboardMetric[]> = {
    users: ["all_stars", "volume", "level"],
    collections: ["all_stars", "volume"],
    nft: ["all_stars"], // NFT uses all_stars as default metric
  };

  // Redirect old query param URLs to new path format and ensure order_param is always in path
  useEffect(() => {
    const sortFromQuery = searchParams.get("sort");

    // If sort is in query params but not in path, redirect to new format
    if (sortFromQuery && !order_param) {
      const sort = sortFromQuery as LeaderboardMetric;
      const newPath =
        currentType === "collections"
          ? `/leaderboard/${sort}`
          : `/leaderboard/${currentType}/${sort}`;

      // Preserve share and addr params
      const shareParam = searchParams.get("share");
      const addrParam = searchParams.get("addr");
      const newSearchParams = new URLSearchParams();
      if (shareParam) newSearchParams.set("share", shareParam);
      if (addrParam) newSearchParams.set("addr", addrParam);

      const queryString = newSearchParams.toString();
      navigate(`${newPath}${queryString ? `?${queryString}` : ""}`, {
        replace: true,
      });
      return;
    }

    // If no order_param in path and no sort in query, redirect to default with order_param
    if (!order_param && !sortFromQuery) {
      const defaultSort = getDefaultSort(currentType);
      const newPath =
        currentType === "collections"
          ? `/leaderboard/${defaultSort}`
          : `/leaderboard/${currentType}/${defaultSort}`;

      // Preserve share and addr params
      const shareParam = searchParams.get("share");
      const addrParam = searchParams.get("addr");
      const newSearchParams = new URLSearchParams();
      if (shareParam) newSearchParams.set("share", shareParam);
      if (addrParam) newSearchParams.set("addr", addrParam);

      const queryString = newSearchParams.toString();
      navigate(`${newPath}${queryString ? `?${queryString}` : ""}`, {
        replace: true,
      });
      return;
    }

    // Validate and redirect if invalid sort param in URL
    if (
      order_param &&
      !validSorts[currentType].includes(order_param as LeaderboardMetric)
    ) {
      const defaultSort = getDefaultSort(currentType);
      const newPath =
        currentType === "collections"
          ? `/leaderboard/${defaultSort}`
          : `/leaderboard/${currentType}/${defaultSort}`;

      // Preserve share and addr params
      const shareParam = searchParams.get("share");
      const addrParam = searchParams.get("addr");
      const newSearchParams = new URLSearchParams();
      if (shareParam) newSearchParams.set("share", shareParam);
      if (addrParam) newSearchParams.set("addr", addrParam);

      const queryString = newSearchParams.toString();
      navigate(`${newPath}${queryString ? `?${queryString}` : ""}`, {
        replace: true,
      });
    }
  }, [order_param, currentType, navigate, searchParams]);

  const handleMenuChange = (menu: MenuType) => {
    const defaultSort = getDefaultSort(menu);
    const newPath =
      menu === "collections"
        ? `/leaderboard/${defaultSort}`
        : `/leaderboard/${menu}/${defaultSort}`;
    navigate(newPath);
  };

  const handleSortChange = (sort: LeaderboardMetric) => {
    const newPath =
      currentType === "collections"
        ? `/leaderboard/${sort}`
        : `/leaderboard/${currentType}/${sort}`;
    navigate(newPath);
  };

  useEffect(() => {
    const shareParam = searchParams.get("share");
    const addrParam = searchParams.get("addr");

    if (shareParam === "true" && addrParam) {
      // Fetch full data by address based on menu type
      const fetchShareData = async () => {
        try {
          let userInfo: LeaderboardItemType | null = null;

          if (currentType === "users") {
            userInfo = await fetchOwnerUserData(addrParam, currentSort);
          } else if (currentType === "collections") {
            userInfo = await fetchCollectionDataByAddress(
              addrParam,
              currentSort
            );
          } else if (currentType === "nft") {
            userInfo = await fetchNftDataByAddress(addrParam);
          }

          if (userInfo) {
            setShareUserInfo(userInfo);
            setShowShareModal(true);
          } else {
            console.error("Could not fetch share data for address:", addrParam);
          }
        } catch (error) {
          console.error("Error fetching shared data:", error);
        }
      };

      fetchShareData();
    }
  }, [searchParams, currentType, currentSort]);

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("share");
    newSearchParams.delete("addr");
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <MainLayout>
      <div className={`${showShareModal ? "blur-[4px]" : ""}`}>
        <LeaderBoard
          selectedMenu={currentType}
          onMenuChange={handleMenuChange}
          metric={currentSort}
          onMetricChange={handleSortChange}
        />
        <MotionModal isOpen={showShareModal && !!shareUserInfo}>
          {shareUserInfo && (
            <ShareInfoModal
              userInfo={shareUserInfo}
              selectedMenu={currentType}
              metric={currentSort}
              onClickClose={handleCloseShareModal}
            />
          )}
        </MotionModal>
      </div>
    </MainLayout>
  );
}
